import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { CardSummary } from '../api/client.ts';
import { api } from '../api/client.ts';
import {
  isBotAutoActivateError,
  type BotAutoActivateError,
  type BotAutoActivateResponse,
} from '../types/bot-auto-activate.ts';

import { publishPhase } from './refetch-interval.ts';


// Bot-proxy pattern: the activation code never crosses the mini-app
// boundary. The orchestrator works exclusively against the boolean
// envelope returned by the bot's /auto-activate endpoint and renders
// nothing that could expose, store, or log a card code.

export type Phase =
  | 'idle'
  | 'working'
  | 'succeeded'
  | 'failed'
  | 'waiting_retry'
  | 'retry_armed';

export type ErrorState =
  | 'rate_limited'
  | 'retrieval_4xx'
  | 'retrieval_5xx'
  | 'already_activated'
  | 'invalid_code_panic'
  | 'activate_uncertain'
  | 'session_expired'
  | 'backgrounded';

export interface OrchestratorReturn {
  readonly phase: Phase;
  readonly errorState: ErrorState | undefined;
  readonly retryAfterSec: number | undefined;
  readonly canRetry: boolean;
  readonly start: () => void;
  readonly retry: () => void;
}

// Module-level guard. Survives React 19 StrictMode double-mount because
// it lives outside the component tree. Cleared by __resetForTests().
const userRetryCountThisSession = new Map<string, number>();

// One retry-tap maximum per session per card. Total bot invocations per card
// per session is therefore at most 2 (the initial Activate tap plus one
// retry tap). Bounds L1 ceiling consumption well under the 3/hour bot cap.
const USER_RETRY_CAP = 1;

// Per-error-class default for retryAfterSec when the bot omits the field.
const DEFAULT_RETRY_AFTER: Readonly<Record<ErrorState, number>> = {
  rate_limited: 3600,
  retrieval_4xx: 0,
  retrieval_5xx: 60,
  already_activated: 0,
  invalid_code_panic: 0,
  activate_uncertain: 300,
  session_expired: 0,
  backgrounded: 0,
};

const TERMINAL_ERRORS: ReadonlySet<ErrorState> = new Set<ErrorState>([
  'retrieval_4xx',
  'invalid_code_panic',
  'session_expired',
]);

// Maps the bot's error catalogue onto the orchestrator's UX-facing failure
// modes. Any unknown code collapses to retrieval_5xx so the user gets a
// retry shot rather than a dead-end.
const mapBotError = (code: BotAutoActivateError): ErrorState => {
  switch (code) {
    case 'CARD_RATE_LIMITED':
    case 'WL_DAILY_CEILING_EXCEEDED':
    case 'USER_DAILY_LIMIT':
    case 'KEY_RATE_LIMITED':
      return 'rate_limited';
    case 'CARD_ACCESS_DENIED':
    case 'CARD_NOT_FOUND':
    case 'CARD_NOT_PHYSICAL':
    case 'CARD_NOT_DELIVERED':
      return 'retrieval_4xx';
    case 'CARD_ALREADY_ACTIVATED':
      return 'already_activated';
    case 'ACTIVATION_TIMEOUT_RETRY_LATER':
      return 'activate_uncertain';
    case 'TELEGRAM_AUTH_FAILED':
      return 'session_expired';
    case 'BOT_KEY_INVALID':
    case 'PROVIDER_ERROR':
    case 'ACTIVATION_CODE_RETRIEVE_FAILED':
    case 'INTERNAL_ERROR':
      return 'retrieval_5xx';
  }
};

// Defensive: reject any payload carrying a code-shaped field — the bot
// must never echo the activation code back to the mini-app boundary.
// Uses exact own-property check (NOT substring match) so future fields
// containing the substring "code" (e.g. errorCode) do not false-positive.
const BANNED_FIELDS: readonly string[] = ['code', 'activationCode', 'pin'];

export const parseBotResponse = (raw: unknown): BotAutoActivateResponse => {
  if (raw === null || typeof raw !== 'object') {
    return { activated: false, error: 'PROVIDER_ERROR' };
  }
  const obj = raw as Record<string, unknown>;
  for (const banned of BANNED_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(obj, banned)) {
      console.warn('auto-activation: bot response contained banned field; treating as failure');
      return { activated: false, error: 'PROVIDER_ERROR' };
    }
  }
  if (obj.activated === true) return { activated: true };
  if (obj.activated === false && isBotAutoActivateError(obj.error)) {
    const retryAfter =
      typeof obj.retryAfter === 'number' && Number.isFinite(obj.retryAfter)
        ? obj.retryAfter
        : undefined;
    return retryAfter !== undefined
      ? { activated: false, error: obj.error, retryAfter }
      : { activated: false, error: obj.error };
  }
  return { activated: false, error: 'PROVIDER_ERROR' };
};

// Tap guard: every condition must hold for the idle → working transition
// that start() drives. Activation keys off the chip-activation signals, NOT
// the REAP card status: a delivered physical card is already ACTIVE for
// online use before its chip is activated, so a delivered, unactivated card
// (physicalCardStatus DELIVERED, physicalActivatedAt null) is exactly the
// one that should activate. Both signals flip once activation lands, which
// bails here and removes the button.
const passesTriggerGuard = (card: CardSummary): boolean => {
  if (card.physicalCardStatus !== 'DELIVERED') return false;
  if (card.physicalActivatedAt !== null && card.physicalActivatedAt !== undefined) return false;
  return true;
};

interface InternalState {
  readonly phase: Phase;
  readonly errorState: ErrorState | undefined;
  readonly retryAfterSec: number | undefined;
  readonly failedAt: number | undefined;
}

const IDLE: InternalState = {
  phase: 'idle',
  errorState: undefined,
  retryAfterSec: undefined,
  failedAt: undefined,
};

export const useAutoActivationOrchestrator = (card: CardSummary): OrchestratorReturn => {
  const queryClient = useQueryClient();
  const [state, setState] = useState<InternalState>(IDLE);
  const cardIdRef = useRef<string>(card.id);
  cardIdRef.current = card.id;

  const mutation = useMutation({
    mutationFn: async (cardId: string): Promise<BotAutoActivateResponse> => {
      const raw = await api<unknown>(`/cards/${encodeURIComponent(cardId)}/auto-activate`, {
        method: 'POST',
      });
      return parseBotResponse(raw);
    },
    onSuccess: (response): void => {
      if (response.activated) {
        setState({
          phase: 'succeeded',
          errorState: undefined,
          retryAfterSec: undefined,
          failedAt: undefined,
        });
        void queryClient.invalidateQueries({ queryKey: ['cards'] });
        return;
      }
      const errorState = mapBotError(response.error);
      if (errorState === 'already_activated') {
        setState({
          phase: 'succeeded',
          errorState: undefined,
          retryAfterSec: undefined,
          failedAt: undefined,
        });
        void queryClient.invalidateQueries({ queryKey: ['cards'] });
        return;
      }
      const retryAfterSec = response.retryAfter ?? DEFAULT_RETRY_AFTER[errorState];
      const userRetryCount = userRetryCountThisSession.get(cardIdRef.current) ?? 0;
      const terminal = TERMINAL_ERRORS.has(errorState) || userRetryCount >= USER_RETRY_CAP;
      setState({
        phase: terminal ? 'failed' : 'waiting_retry',
        errorState,
        retryAfterSec,
        failedAt: terminal ? undefined : Date.now(),
      });
    },
    onError: (): void => {
      // Network / fetch failure outside the bot envelope. Treat as a
      // retrievable 5xx so the user gets one shot to recover rather
      // than a permanent dead end.
      const userRetryCount = userRetryCountThisSession.get(cardIdRef.current) ?? 0;
      const terminal = userRetryCount >= USER_RETRY_CAP;
      setState({
        phase: terminal ? 'failed' : 'waiting_retry',
        errorState: 'retrieval_5xx',
        retryAfterSec: DEFAULT_RETRY_AFTER.retrieval_5xx,
        failedAt: terminal ? undefined : Date.now(),
      });
    },
  });

  // Explicit-tap trigger. Activation never fires on render — the user taps
  // the Activate button, which calls this. The ref guard fires exactly once
  // per mount even if two taps land in the same React batch (where the
  // phase state read is still the stale 'idle'); the phase check then blocks
  // any later tap, and remounting resets the ref for a fresh attempt.
  const startedRef = useRef(false);
  const start = useCallback((): void => {
    if (startedRef.current) return;
    if (state.phase !== 'idle') return;
    if (!passesTriggerGuard(card)) return;
    startedRef.current = true;
    setState({
      phase: 'working',
      errorState: undefined,
      retryAfterSec: undefined,
      failedAt: undefined,
    });
    mutation.mutate(card.id);
  }, [state.phase, card, mutation]);

  // Timer: when in waiting_retry and the back-off elapses, advance to
  // retry_armed so the CTA enables. The hook NEVER auto-fires the
  // mutation here — only an explicit user tap.
  useEffect(() => {
    if (state.phase !== 'waiting_retry') return;
    if (state.failedAt === undefined) return;
    const target = state.failedAt + (state.retryAfterSec ?? 0) * 1000;
    const remaining = target - Date.now();
    if (remaining <= 0) {
      setState((prev) => ({ ...prev, phase: 'retry_armed' }));
      return;
    }
    const handle = setTimeout(() => {
      setState((prev) => ({ ...prev, phase: 'retry_armed' }));
    }, remaining);
    return (): void => {
      clearTimeout(handle);
    };
  }, [state.phase, state.failedAt, state.retryAfterSec]);

  // Publish per-card phase into the shared query cache so the cards
  // refetch interval can react to it. One-direction dependency:
  // orchestrator writes, refetch reads.
  useEffect(() => {
    publishPhase(queryClient, card.id, state.phase);
  }, [queryClient, card.id, state.phase]);

  const userRetryCount = userRetryCountThisSession.get(card.id) ?? 0;
  const canRetry = state.phase === 'retry_armed' && userRetryCount < USER_RETRY_CAP;

  const retry = useCallback((): void => {
    if (state.phase !== 'retry_armed') return;
    const currentCount = userRetryCountThisSession.get(cardIdRef.current) ?? 0;
    if (currentCount >= USER_RETRY_CAP) return;
    userRetryCountThisSession.set(cardIdRef.current, currentCount + 1);
    setState({
      phase: 'working',
      errorState: undefined,
      retryAfterSec: undefined,
      failedAt: undefined,
    });
    mutation.mutate(cardIdRef.current);
  }, [state.phase, mutation]);

  return {
    phase: state.phase,
    errorState: state.errorState,
    retryAfterSec: state.retryAfterSec,
    canRetry,
    start,
    retry,
  };
};

// Test-only helper: clears the module-level retry counter so consecutive
// tests see a pristine cap. Not exported via the package index.
export const __resetForTests = (): void => {
  userRetryCountThisSession.clear();
};
